import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  MenuItem,
  Button,
  Grid,
  Card,
  CardContent,
  Alert,
  Collapse,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButtonGroup,
  ToggleButton,
  useMediaQuery,
  useTheme,
  Checkbox,
  ListItemText,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Speed as EfficiencyIcon,
  AccessTime as TimeIcon,
  Build as MachineIcon,
  Work as JobIcon,
  Refresh as RefreshIcon,
  BarChart as ChartIcon,
  TableChart as TableIcon,
  ViewTimeline as TimelineIcon,
  GetApp as DownloadIcon,
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import api from '../services/api';
import { format, parse, differenceInMinutes, addDays } from 'date-fns';
import { useAuthStore } from '../store/authStore';

interface MachineEfficiencyEntry {
  id?: number;
  machine: string;
  job_type: string;
  time_start: string;
  time_stop: string;
  artwork_no?: string;
  batch_no?: string;
  epf_number?: string;
  machine_speed?: number;
  quantity?: number;
  date: string;
  duration_minutes?: number;
  created_by?: string;
  created_at?: string;
}

const machines = [
  'R1',
  'R4',
  'R5',
  'Steamer 01',
  'Steamer 02',
  '48-Digital',
  'MS-Digital',
  'Sublimation',
  'Washer',
];

// Machine-specific job types
const machineJobTypes: { [key: string]: string[] } = {
  '48-Digital': [
    'M/C Cleaning (TPM)',
    'Head Cleaning & Test Draw Print',
    'Blanket season & check',
    'One yard print',
    'More than one yard print',
    'Less Work(Machine ON)',
    'Less Work(Machine OFF)',
    'W/F color matching',
    'Color Check And Adjustment',
    'Color Check And Adjustment(More than one yrd)',
    'E&M Repair',
    'One yard Steaming and washing',
    'Option Printing',
    'Option steaming & washing',
    'More than one yard Steaming & Washing',
    'one yard steaming & washing',
    'Bulk Runing/Sample Runing',
    'Tea Time / Lunch Time',
  ],
  'MS-Digital': [
    'M/C Cleaning (TPM)',
    'Head Cleaning & Test Draw Print',
    'Blanket season & check',
    'One yard print',
    'More than one yard print',
    'Less Work(Machine ON)',
    'Less Work(Machine OFF)',
    'W/F color matching',
    'Color Check And Adjustment',
    'Color Check And Adjustment(More than one yrd)',
    'E&M Repair',
    'One yard Steaming and washing',
    'Option Printing',
    'Option steaming & washing',
    'More than one yard Steaming & Washing',
    'one yard steaming & washing',
    'Bulk Runing/Sample Runing',
    'Tea Time / Lunch Time',
  ],
  'Sublimation': [
    'M/C Cleaning (TPM)',
    'Head Cleaning & Test Draw Print',
    'Blanket season & check',
    'One yard print',
    'More than one yard print',
    'Less Work(Machine ON)',
    'Less Work(Machine OFF)',
    'W/F color matching',
    'Color Check And Adjustment',
    'Color Check And Adjustment(More than one yrd)',
    'E&M Repair',
    'One yard Steaming and washing',
    'Option Printing',
    'Option steaming & washing',
    'More than one yard Steaming & Washing',
    'one yard steaming & washing',
    'Bulk Runing/Sample Runing',
    'Tea Time / Lunch Time',
  ],
  'R1': [
    'M/C Cleaning (TPM)',
    'M/C Cleaning (More than given time)',
    'Blanket season & check',
    'One yard print',
    'One yard print (Waiting for Screen)',
    'One yard print (Waiting for Paste)',
    'Second yard print',
    'Second yard print (Waiting for Screen)',
    'Second yard print (Waiting for Paste)',
    'More than Second yard print',
    'More than Second yard print (Waiting for Screen)',
    'More than Second yard print (Waiting for Paste)',
    'Screen remove and fix - Bulk/Sample/1yard',
    'Screen remove and fix - More than 1 yard',
    'Less work (Machine ON)',
    'Screen Damage (M)',
    'Screen block (M / PFP )',
    'Paste filter',
    'Paste filter (More than given time)',
    'Pad Print (Reprocess)',
    'Digital Pad Print',
    'West print',
    'W/F color matching',
    'W/F color matching (More than given time)',
    'Waiting For paste',
    'Waiting For paste(More than one yard)',
    'Color Check And Adjustment',
    'Color Check And Adjustment(More than one yard)',
    'E&M Repair',
    'W/F screen',
    'Screen Damage (S)',
    'Screen block (S)',
    'Screen Head Change',
    'Bulk & Sample Print Time',
    'Bulk & Sample Print Time (Waiting for Screen)',
    'Bulk & Sample Print Time (Waiting for Paste)',
    'M/C Off Time',
    'Tea Time / Lunch Time',
  ],
  'R4': [
    'M/C Cleaning (TPM)',
    'M/C Cleaning (More than given time)',
    'Blanket season & check',
    'One yard print',
    'One yard print (Waiting for Screen)',
    'One yard print (Waiting for Paste)',
    'Second yard print',
    'Second yard print (Waiting for Screen)',
    'Second yard print (Waiting for Paste)',
    'More than Second yard print',
    'More than Second yard print (Waiting for Screen)',
    'More than Second yard print (Waiting for Paste)',
    'Screen remove and fix - Bulk/Sample/1yard',
    'Screen remove and fix - More than 1 yard',
    'Less work (Machine ON)',
    'Screen Damage (M)',
    'Screen block (M / PFP )',
    'Paste filter',
    'Paste filter (More than given time)',
    'Pad Print (Reprocess)',
    'Digital Pad Print',
    'West print',
    'W/F color matching',
    'W/F color matching (More than given time)',
    'Waiting For paste',
    'Waiting For paste(More than one yard)',
    'Color Check And Adjustment',
    'Color Check And Adjustment(More than one yard)',
    'E&M Repair',
    'W/F screen',
    'Screen Damage (S)',
    'Screen block (S)',
    'Screen Head Change',
    'Bulk & Sample Print Time',
    'Bulk & Sample Print Time (Waiting for Screen)',
    'Bulk & Sample Print Time (Waiting for Paste)',
    'M/C Off Time',
    'Tea Time / Lunch Time',
  ],
  'R5': [
    'M/C Cleaning (TPM)',
    'M/C Cleaning (More than given time)',
    'Blanket season & check',
    'One yard print',
    'One yard print (Waiting for Screen)',
    'One yard print (Waiting for Paste)',
    'Second yard print',
    'Second yard print (Waiting for Screen)',
    'Second yard print (Waiting for Paste)',
    'More than Second yard print',
    'More than Second yard print (Waiting for Screen)',
    'More than Second yard print (Waiting for Paste)',
    'Screen remove and fix - Bulk/Sample/1yard',
    'Screen remove and fix - More than 1 yard',
    'Less work (Machine ON)',
    'Screen Damage (M)',
    'Screen block (M / PFP )',
    'Paste filter',
    'Paste filter (More than given time)',
    'Pad Print (Reprocess)',
    'Digital Pad Print',
    'West print',
    'W/F color matching',
    'W/F color matching (More than given time)',
    'Waiting For paste',
    'Waiting For paste(More than one yard)',
    'Color Check And Adjustment',
    'Color Check And Adjustment(More than one yard)',
    'E&M Repair',
    'W/F screen',
    'Screen Damage (S)',
    'Screen block (S)',
    'Screen Head Change',
    'Bulk & Sample Print Time',
    'Bulk & Sample Print Time (Waiting for Screen)',
    'Bulk & Sample Print Time (Waiting for Paste)',
    'M/C Off Time',
    'Tea Time / Lunch Time',
  ],
  'Steamer 01': [
    'Cleaning(TPM)',
    'Cleaning(More than Given time)',
    'E&M Repaire',
    'M/C Roll Cleaning',
    'M/C heating',
    'Change Temparature',
    'Low temparature (boiler issue)',
    'Boiler repair',
    'M/C cooling',
    'one yard running',
    'Color check',
    'waste fabric running',
    'less work(Machine On)',
    'Color not ok',
    'Running time',
    'M/C Off time',
    'Tea Time / Lunch Time',
  ],
  'Steamer 02': [
    'Cleaning(TPM)',
    'Cleaning(More than Given time)',
    'E&M Repaire',
    'M/C Roll Cleaning',
    'M/C heating',
    'Change Temparature',
    'Low temparature (boiler issue)',
    'Boiler repair',
    'M/C cooling',
    'one yard running',
    'Color check',
    'waste fabric running',
    'less work(Machine On)',
    'Color not ok',
    'Running time',
    'M/C Off time',
    'Tea Time / Lunch Time',
  ],
  'Washer': [
    'M/C Cleaning (TPM)',
    'M/C Cleaning (More than given time)',
    'E&M repair',
    'machine drain and cleaning',
    'm/c heating',
    'Machine Water Drain & New Water Filling',
    'Washer m/c new camicle add',
    '1 yards washing 1st time',
    'More than 1 yard',
    'Color Check',
    'Color Check (More than given time)',
    'Less work(Machine On)',
    'Re wash',
    'W/F water',
    'W/F steamer card',
    'Waiting For Trolly',
    'Waste running',
    'Running time',
    'M/C Off time',
    'Tea Time / Lunch Time',
  ],
};

// Job types that require additional inputs (artwork, batch, speed)
const productionJobTypes = [
  'One yard print',
  'More than one yard print',
  'Second yard print',
  'More than Second yard print',
  'Screen remove and fix - Bulk/Sample/1yard',
  'Screen remove and fix - More than 1 yard',
  '1 yards washing 1st time',
  'More than 1 yard',
  'One yard Steaming and washing',
  'More than one yard Steaming & Washing',
  'one yard steaming & washing',
  'Bulk Runing/Sample Runing',
  'Option Printing',
  'Bulk & Sample Print Time',
  'one yard running',
  'Running time',
];

const PRODUCTION_DAY_RESET_MINUTES = 7 * 60 + 30; // 07:30

// Returns the position of a time string in the production day timeline (0-100%).
// Production day starts at 07:30, so times >= 07:30 map to the first part and
// times < 07:30 map to the second part (after midnight).
const toProductionMinutes = (timeStr: string): number => {
  const [h, m] = timeStr.split(':').map(Number);
  const total = h * 60 + m;
  return total >= PRODUCTION_DAY_RESET_MINUTES
    ? total - PRODUCTION_DAY_RESET_MINUTES
    : total + (24 * 60 - PRODUCTION_DAY_RESET_MINUTES);
};

// Production day runs from 07:30 on Day X to 07:29 on Day X+1.
// When before 07:30, the current production day started on the previous calendar day.
const getProductionDayDate = (): Date => {
  const now = new Date();
  if (now.getHours() < 7 || (now.getHours() === 7 && now.getMinutes() < 30)) {
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday;
  }
  return now;
};

// For an entry with a given time, determine the actual calendar date to store.
// Entries before 07:30 belong to the previous production day but their
// calendar date is selectedDate + 1 so the backend query can find them.
const getEntryCalendarDate = (productionDate: Date, entryTime: Date): Date => {
  const h = entryTime.getHours();
  const m = entryTime.getMinutes();
  if (h < 7 || (h === 7 && m < 30)) {
    return addDays(productionDate, 1);
  }
  return productionDate;
};

const MachineEfficiency: React.FC = () => {
  const { user } = useAuthStore();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [entries, setEntries] = useState<MachineEfficiencyEntry[]>([]);
  const [filteredEntries, setFilteredEntries] = useState<MachineEfficiencyEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<MachineEfficiencyEntry | null>(null);
  const [editDate, setEditDate] = useState<Date>(new Date());
  const [selectedDate, setSelectedDate] = useState<Date>(getProductionDayDate());
  const [filterMachine, setFilterMachine] = useState<string>('All');
  const [viewMode, setViewMode] = useState<'timeline' | 'table'>('timeline');

  // CSV export state
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportMachine, setExportMachine] = useState<string>('R4');
  const [exportStartDate, setExportStartDate] = useState<Date>(new Date());
  const [exportEndDate, setExportEndDate] = useState<Date>(new Date());
  const [exportGroup1Name, setExportGroup1Name] = useState('Group 1');
  const [exportGroup2Name, setExportGroup2Name] = useState('Group 2');
  const [exportGroup1Jobs, setExportGroup1Jobs] = useState<string[]>([]);
  const [exportGroup2Jobs, setExportGroup2Jobs] = useState<string[]>([]);
  const [exportGroup2Mode, setExportGroup2Mode] = useState<'all-others' | 'manual'>('all-others');
  const [exportLoading, setExportLoading] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  // OEE Export state
  const [oeeExportDialogOpen, setOeeExportDialogOpen] = useState(false);
  const [oeeExportMachines, setOeeExportMachines] = useState<string[]>(['R4']);
  const [oeeExportMonth, setOeeExportMonth] = useState<Date>(new Date());
  const [oeeRunningJobs, setOeeRunningJobs] = useState<string[]>(() => {
    const saved = localStorage.getItem('oeeRunningJobs');
    return saved ? JSON.parse(saved) : [];
  });
  const [oeeLossJobs, setOeeLossJobs] = useState<string[]>(() => {
    const saved = localStorage.getItem('oeeLossJobs');
    return saved ? JSON.parse(saved) : [];
  });
  const [oeePdJobs, setOeePdJobs] = useState<string[]>(() => {
    const saved = localStorage.getItem('oeePdJobs');
    return saved ? JSON.parse(saved) : [];
  });
  const [oeeExportLoading, setOeeExportLoading] = useState(false);
  const [oeeExportError, setOeeExportError] = useState<string | null>(null);

  // Get all unique jobs from selected machines
  const oeeAvailableJobs = Array.from(
    new Set(
      oeeExportMachines.flatMap(machine => machineJobTypes[machine] || [])
    )
  ).sort();

  const exportAvailableJobs = exportMachine ? (machineJobTypes[exportMachine] || []) : [];

  const toggleExportJob = (job: string, group: 1 | 2) => {
    if (group === 1) {
      setExportGroup1Jobs(prev =>
        prev.includes(job) ? prev.filter(j => j !== job) : [...prev, job]
      );
      // Remove from group2 if added to group1
      setExportGroup2Jobs(prev => prev.filter(j => j !== job));
    } else {
      setExportGroup2Jobs(prev =>
        prev.includes(job) ? prev.filter(j => j !== job) : [...prev, job]
      );
      // Remove from group1 if added to group2
      setExportGroup1Jobs(prev => prev.filter(j => j !== job));
    }
  };

  const getJobExportBucket = (job: string): 'g1' | 'g2' | 'none' => {
    if (exportGroup1Jobs.includes(job)) return 'g1';
    if (exportGroup2Jobs.includes(job)) return 'g2';
    return 'none';
  };

  const handleExportCSV = async () => {
    if (!exportMachine) { setExportError('Please select a machine'); return; }
    if (exportGroup1Jobs.length === 0) { setExportError('Please assign at least one job to Group 1'); return; }
    setExportLoading(true);
    setExportError(null);
    try {
      const startStr = format(exportStartDate, 'yyyy-MM-dd');
      const endStr = format(exportEndDate, 'yyyy-MM-dd');
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        machine: exportMachine,
        start_date: startStr,
        end_date: endStr,
        group1_jobs: exportGroup1Jobs.join(','),
        group1_name: exportGroup1Name,
        group2_name: exportGroup2Name,
        ...(exportGroup2Mode === 'manual' && exportGroup2Jobs.length > 0
          ? { group2_jobs: exportGroup2Jobs.join(',') }
          : {}),
      });
      const response = await fetch(
        `/api/machine-efficiency/job-comparison-csv?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({ message: 'Failed to generate CSV' }));
        throw new Error(err.message || 'Failed to generate CSV');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${exportMachine}_job_comparison_${startStr}_to_${endStr}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setExportDialogOpen(false);
    } catch (err: any) {
      setExportError(err.message || 'Failed to generate CSV');
    } finally {
      setExportLoading(false);
    }
  };

  // OEE Job Categorization (Running, Loss, and PD are manually assigned)
  const toggleOeeJob = (job: string, category: 'running' | 'loss' | 'pd') => {
    // Remove from both categories first
    const newRunning = oeeRunningJobs.filter(j => j !== job);
    const newLoss = oeeLossJobs.filter(j => j !== job);
    const newPd = oeePdJobs.filter(j => j !== job);

    // Add to selected category and save to localStorage
    if (category === 'running') {
      const updated = [...newRunning, job];
      setOeeRunningJobs(updated);
      localStorage.setItem('oeeRunningJobs', JSON.stringify(updated));
      setOeeLossJobs(newLoss);
      localStorage.setItem('oeeLossJobs', JSON.stringify(newLoss));
      setOeePdJobs(newPd);
      localStorage.setItem('oeePdJobs', JSON.stringify(newPd));
    } else if (category === 'loss') {
      const updated = [...newLoss, job];
      setOeeRunningJobs(newRunning);
      localStorage.setItem('oeeRunningJobs', JSON.stringify(newRunning));
      setOeeLossJobs(updated);
      localStorage.setItem('oeeLossJobs', JSON.stringify(updated));
      setOeePdJobs(newPd);
      localStorage.setItem('oeePdJobs', JSON.stringify(newPd));
    } else if (category === 'pd') {
      const updated = [...newPd, job];
      setOeeRunningJobs(newRunning);
      localStorage.setItem('oeeRunningJobs', JSON.stringify(newRunning));
      setOeeLossJobs(newLoss);
      localStorage.setItem('oeeLossJobs', JSON.stringify(newLoss));
      setOeePdJobs(updated);
      localStorage.setItem('oeePdJobs', JSON.stringify(updated));
    }
  };

  const getOeeJobCategory = (job: string): 'running' | 'loss' | 'pd' | 'none' => {
    if (oeeRunningJobs.includes(job)) return 'running';
    if (oeeLossJobs.includes(job)) return 'loss';
    if (oeePdJobs.includes(job)) return 'pd';
    return 'none';
  };

  const handleExportOEE = async () => {
    if (oeeExportMachines.length === 0) {
      setOeeExportError('Please select at least one machine');
      return;
    }
    if (oeeRunningJobs.length === 0) {
      setOeeExportError('Please assign at least one job to Running category');
      return;
    }
    if (oeeLossJobs.length === 0) {
      setOeeExportError('Please assign at least one job to Loss category');
      return;
    }

    setOeeExportLoading(true);
    setOeeExportError(null);

    try {
      const month = oeeExportMonth.getMonth() + 1;
      const year = oeeExportMonth.getFullYear();
      const token = localStorage.getItem('token');

      const params = new URLSearchParams({
        machines: oeeExportMachines.join(','),
        month: month.toString(),
        year: year.toString(),
        running_jobs: oeeRunningJobs.join(','),
        loss_jobs: oeeLossJobs.join(','),
        pd_jobs: oeePdJobs.join(','),
      });

      const response = await fetch(
        `/api/machine-efficiency/oee-csv-export?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const err = await response.json().catch(() => ({ message: 'Failed to generate OEE export' }));
        throw new Error(err.message || 'Failed to generate OEE export');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      a.download = `OEE_${monthNames[month - 1]}_${year}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      setOeeExportDialogOpen(false);
    } catch (err: any) {
      setOeeExportError(err.message || 'Failed to generate OEE export');
    } finally {
      setOeeExportLoading(false);
    }
  };

  // Form fields
  const [machine, setMachine] = useState<string>('');
  const [jobType, setJobType] = useState<string>('');
  const [timeStart, setTimeStart] = useState<Date | null>(null);
  const [timeStop, setTimeStop] = useState<Date | null>(null);
  const [artworkNo, setArtworkNo] = useState<string>('');
  const [batchNo, setBatchNo] = useState<string>('');
  const [epfNumber, setEpfNumber] = useState<string>('');
  const [machineSpeed, setMachineSpeed] = useState<string>('');
  const [quantity, setQuantity] = useState<string>('');

  const requiresProductionInputs = productionJobTypes.includes(jobType);

  // Get job types for the selected machine
  const availableJobTypes = machine ? (machineJobTypes[machine] || []) : [];

  // Auto-set filter and machine for machine operators
  useEffect(() => {
    if (user?.role === 'machine' && user?.assigned_machine) {
      setFilterMachine(user.assigned_machine);
      setMachine(user.assigned_machine);
    }
  }, [user]);

  useEffect(() => {
    fetchEntries();
  }, [selectedDate]);

  useEffect(() => {
    filterEntriesByMachine();
  }, [entries, filterMachine]);

  const fetchEntries = async () => {
    setLoading(true);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const response = await api.get(`/machine-efficiency?date=${dateStr}`);
      setEntries(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch entries');
    } finally {
      setLoading(false);
    }
  };

  const filterEntriesByMachine = () => {
    if (filterMachine === 'All') {
      setFilteredEntries(entries);
    } else {
      setFilteredEntries(entries.filter((entry) => entry.machine === filterMachine));
    }
  };

  const handleOpenDialog = () => {
    resetForm();
    // Re-set machine for machine operators after resetForm
    if (user?.role === 'machine' && user?.assigned_machine) {
      setMachine(user.assigned_machine);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    resetForm();
  };

  const resetForm = () => {
    // For machine operators, preserve their assigned machine
    if (user?.role === 'machine' && user?.assigned_machine) {
      setMachine(user.assigned_machine);
    } else {
      setMachine('');
    }
    setJobType('');
    setTimeStart(null);
    setTimeStop(null);
    setArtworkNo('');
    setBatchNo('');
    setEpfNumber('');
    setMachineSpeed('');
    setQuantity('');
  };

  const handleSubmit = async () => {
    if (!machine || !jobType || !timeStart || !timeStop) {
      setError('Please fill in all required fields');
      return;
    }

    if (requiresProductionInputs && (!artworkNo || !batchNo || !machineSpeed || !quantity)) {
      setError('Please fill in Artwork No., Batch No., Machine Speed, and Quantity for production jobs');
      return;
    }

    if (timeStop <= timeStart) {
      const startMin = timeStart.getHours() * 60 + timeStart.getMinutes();
      const stopMin  = timeStop.getHours()  * 60 + timeStop.getMinutes();
      if (startMin >= PRODUCTION_DAY_RESET_MINUTES && stopMin < PRODUCTION_DAY_RESET_MINUTES) {
        // ── Auto-split across midnight ──────────────────────────────────────
        const date1Str = format(selectedDate, 'yyyy-MM-dd');
        const date2Str = format(addDays(selectedDate, 1), 'yyyy-MM-dd');
        const start1   = format(timeStart, 'HH:mm:ss');
        const stop1    = '23:59:59';
        const start2   = '00:00:00';
        const stop2    = format(timeStop, 'HH:mm:ss');
        const isSteamer = machine === 'Steamer 01' || machine === 'Steamer 02';
        const MAX_STEAMER_OVERLAP_MINUTES = 60;

        const checkOverlap = (startStr: string, stopStr: string, dateStr: string) =>
          entries.find(entry => {
            if (entry.machine !== machine || entry.date !== dateStr) return false;
            const hasOverlap = startStr < entry.time_stop && stopStr > entry.time_start;
            if (!hasOverlap) return false;
            if (isSteamer) {
              const overlapStart = startStr > entry.time_start ? startStr : entry.time_start;
              const overlapEnd   = stopStr  < entry.time_stop  ? stopStr  : entry.time_stop;
              const overlapMins  = differenceInMinutes(
                parse(overlapEnd,   'HH:mm:ss', new Date()),
                parse(overlapStart, 'HH:mm:ss', new Date()),
              );
              return overlapMins > MAX_STEAMER_OVERLAP_MINUTES;
            }
            return true;
          });

        const ov1 = checkOverlap(start1, stop1, date1Str);
        const ov2 = checkOverlap(start2, stop2, date2Str);
        if (ov1) { setError(`Time overlap with existing entry: ${ov1.job_type} (${ov1.time_start} - ${ov1.time_stop})`); return; }
        if (ov2) { setError(`Time overlap with existing entry (next day): ${ov2.job_type} (${ov2.time_start} - ${ov2.time_stop})`); return; }

        setLoading(true);
        setError(null);
        try {
          const base: any = { machine, job_type: jobType };
          if (requiresProductionInputs) {
            base.artwork_no     = artworkNo;
            base.batch_no       = batchNo;
            base.epf_number     = epfNumber;
            base.machine_speed  = parseFloat(machineSpeed);
            base.quantity       = parseFloat(quantity);
          }
          await api.post('/machine-efficiency', { ...base, time_start: start1, time_stop: stop1, date: date1Str });
          await api.post('/machine-efficiency', { ...base, time_start: start2, time_stop: stop2, date: date2Str });
          setSuccess(
            `Midnight split saved: ${format(timeStart, 'HH:mm')}–23:59 on ${format(selectedDate, 'MMM dd')} ` +
            `and 00:00–${format(timeStop, 'HH:mm')} on ${format(addDays(selectedDate, 1), 'MMM dd')}`
          );
          handleCloseDialog();
          fetchEntries();
        } catch (err: any) {
          setError(err.response?.data?.message || 'Failed to add entry');
        } finally {
          setLoading(false);
        }
        return;
        // ───────────────────────────────────────────────────────────────────
      } else {
        setError('End time must be after start time');
      }
      return;
    }

    // Check for overlapping entries
    const newStartStr = format(timeStart, 'HH:mm:ss');
    const newStopStr = format(timeStop, 'HH:mm:ss');
    // For midnight entries (before 07:30) the actual stored calendar date is selectedDate+1
    const entryCalendarDate = getEntryCalendarDate(selectedDate, timeStart);
    const entryDateStr = format(entryCalendarDate, 'yyyy-MM-dd');
    const isSteamer = machine === 'Steamer 01' || machine === 'Steamer 02';
    const MAX_STEAMER_OVERLAP_MINUTES = 60; // 1 hour overlap allowed for steamers
    
    const overlapping = entries.find(entry => {
      if (entry.machine !== machine || entry.date !== entryDateStr) {
        return false;
      }
      
      // Check if time ranges overlap
      // Two ranges overlap if: (start1 < end2) AND (start2 < end1)
      const hasOverlap = (newStartStr < entry.time_stop && newStopStr > entry.time_start);
      
      if (!hasOverlap) {
        return false;
      }
      
      // For steamers, allow overlap up to 1 hour due to hold time
      if (isSteamer) {
        // Calculate the actual overlap duration
        const overlapStart = newStartStr > entry.time_start ? newStartStr : entry.time_start;
        const overlapEnd = newStopStr < entry.time_stop ? newStopStr : entry.time_stop;
        
        // Parse times to calculate overlap in minutes
        const overlapStartTime = parse(overlapStart, 'HH:mm:ss', new Date());
        const overlapEndTime = parse(overlapEnd, 'HH:mm:ss', new Date());
        const overlapMinutes = differenceInMinutes(overlapEndTime, overlapStartTime);
        
        // Allow overlap if it's within the 1-hour limit
        return overlapMinutes > MAX_STEAMER_OVERLAP_MINUTES;
      }
      
      // For non-steamers, any overlap is not allowed
      return true;
    });

    if (overlapping) {
      if (isSteamer) {
        setError(`Steamer overlap exceeds 1 hour limit with: ${overlapping.job_type} (${overlapping.time_start} - ${overlapping.time_stop})`);
      } else {
        setError(`Time overlap with existing entry: ${overlapping.job_type} (${overlapping.time_start} - ${overlapping.time_stop})`);
      }
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Use entryCalendarDate (already computed above for the overlap check)
      const dateStr = entryDateStr;
      const timeStartStr = format(timeStart, 'HH:mm:ss');
      const timeStopStr = format(timeStop, 'HH:mm:ss');

      const payload: any = {
        machine,
        job_type: jobType,
        time_start: timeStartStr,
        time_stop: timeStopStr,
        date: dateStr,
      };

      if (requiresProductionInputs) {
        payload.artwork_no = artworkNo;
        payload.batch_no = batchNo;
        payload.epf_number = epfNumber;
        payload.machine_speed = parseFloat(machineSpeed);
        payload.quantity = parseFloat(quantity);
      }

      await api.post('/machine-efficiency', payload);

      setSuccess('Entry added successfully');
      handleCloseDialog();
      fetchEntries();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to add entry');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEditDialog = (entry: MachineEfficiencyEntry) => {
    setEditingEntry(entry);
    setMachine(entry.machine);
    setJobType(entry.job_type);
    const [h1, m1] = entry.time_start.split(':').map(Number);
    const [h2, m2] = entry.time_stop.split(':').map(Number);
    const startD = new Date(); startD.setHours(h1, m1, 0, 0);
    const stopD = new Date(); stopD.setHours(h2, m2, 0, 0);
    setTimeStart(startD);
    setTimeStop(stopD);
    const [yr, mo, dy] = entry.date.split('-').map(Number);
    setEditDate(new Date(yr, mo - 1, dy));
    setArtworkNo(entry.artwork_no || '');
    setBatchNo(entry.batch_no || '');
    setEpfNumber(entry.epf_number || '');
    setMachineSpeed(entry.machine_speed ? String(entry.machine_speed) : '');
    setQuantity(entry.quantity ? String(entry.quantity) : '');
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setEditingEntry(null);
    resetForm();
  };

  const handleEditSubmit = async () => {
    if (!machine || !jobType || !timeStart || !timeStop) {
      setError('Please fill in all required fields');
      return;
    }
    if (requiresProductionInputs && (!artworkNo || !batchNo || !machineSpeed || !quantity)) {
      setError('Please fill in Artwork No., Batch No., Machine Speed, and Quantity for production jobs');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const dateStr = format(editDate, 'yyyy-MM-dd');
      const timeStartStr = format(timeStart, 'HH:mm:ss');
      const timeStopStr = format(timeStop, 'HH:mm:ss');
      const payload: any = {
        machine,
        job_type: jobType,
        time_start: timeStartStr,
        time_stop: timeStopStr,
        date: dateStr,
        artwork_no: artworkNo || null,
        batch_no: batchNo || null,
        epf_number: epfNumber || null,
        machine_speed: machineSpeed ? parseFloat(machineSpeed) : null,
        quantity: quantity ? parseFloat(quantity) : null,
      };
      await api.put(`/machine-efficiency/${editingEntry!.id}`, payload);
      setSuccess('Entry updated successfully');
      handleCloseEditDialog();
      fetchEntries();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update entry');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;

    setLoading(true);
    try {
      await api.delete(`/machine-efficiency/${id}`);
      setSuccess('Entry deleted successfully');
      fetchEntries();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete entry');
    } finally {
      setLoading(false);
    }
  };

  const getJobColor = (jobType: string): string => {
    if (jobType.toLowerCase().includes('cleaning') || jobType.toLowerCase().includes('tpm')) return '#4CAF50';
    if (jobType.toLowerCase().includes('print') || jobType.toLowerCase().includes('running')) return '#2196F3';
    if (jobType.toLowerCase().includes('waiting') || jobType.toLowerCase().includes('w/f')) return '#FF9800';
    if (jobType.toLowerCase().includes('repair') || jobType.toLowerCase().includes('damage')) return '#F44336';
    if (jobType.toLowerCase().includes('color check') || jobType.toLowerCase().includes('adjustment')) return '#9C27B0';
    if (jobType.toLowerCase().includes('less work')) return '#FFD700';
    return '#607D8B';
  };

  const calculateTimelinePosition = (timeStr: string) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    const totalMinutes = hours * 60 + minutes;
    
    return (toProductionMinutes(`${hours}:${minutes}`) / 1440) * 100;
  };

  const renderTimelineBar = (entry: MachineEfficiencyEntry) => {
    const startPos = calculateTimelinePosition(entry.time_start);
    const endPos = calculateTimelinePosition(entry.time_stop);
    const width = endPos - startPos;

    return (
      <Box
        key={entry.id}
        sx={{
          position: 'absolute',
          left: `${startPos}%`,
          width: `${width}%`,
          height: '100%',
          backgroundColor: getJobColor(entry.job_type),
          borderRadius: 1,
          cursor: 'pointer',
          transition: 'all 0.2s',
          '&:hover': {
            opacity: 0.8,
            transform: 'scaleY(1.1)',
          },
        }}
        title={`${entry.job_type} (${entry.time_start} - ${entry.time_stop})`}
      />
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EfficiencyIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: '#00695C' }} />
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#00695C', fontSize: { xs: '1.5rem', sm: '2rem' } }}>
              Machine Efficiency Management
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {(user?.role === 'admin' || user?.role === 'manager') && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => { setOeeExportError(null); setOeeExportMachines(['R4']); setOeeExportDialogOpen(true); }}
                  sx={{
                    borderColor: '#00695C',
                    color: '#00695C',
                    fontSize: { xs: '0.8rem', sm: '0.9rem' },
                    px: { xs: 1.5, sm: 2 },
                    '&:hover': {
                      borderColor: '#004d40',
                      backgroundColor: 'rgba(0, 105, 92, 0.04)',
                    },
                  }}
                >
                  OEE Export
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => { setExportError(null); setExportGroup1Jobs([]); setExportGroup2Jobs([]); setExportDialogOpen(true); }}
                  sx={{
                    borderColor: '#00695C',
                    color: '#00695C',
                    fontSize: { xs: '0.8rem', sm: '0.9rem' },
                    px: { xs: 1.5, sm: 2 },
                  }}
                >
                  Export CSV
                </Button>
              </>
            )}
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenDialog}
              sx={{
                backgroundColor: '#00695C',
                '&:hover': { backgroundColor: '#004d40' },
                fontSize: { xs: '0.875rem', sm: '1rem' },
                px: { xs: 2, sm: 3 },
              }}
            >
              Add Entry
            </Button>
          </Box>
        </Box>

        {/* Success/Error Messages */}
        <Collapse in={!!success}>
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        </Collapse>
        <Collapse in={!!error && !dialogOpen}>
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        </Collapse>

        {/* Filters and View Toggle */}
        <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={(e, newMode) => {
                    if (newMode !== null) {
                      setViewMode(newMode);
                    }
                  }}
                  size="small"
                  sx={{ backgroundColor: 'white' }}
                >
                  <ToggleButton value="timeline" sx={{ px: 3 }}>
                    <TimelineIcon sx={{ mr: 1 }} />
                    Timeline
                  </ToggleButton>
                  <ToggleButton value="table" sx={{ px: 3 }}>
                    <TableIcon sx={{ mr: 1 }} />
                    Table
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <DatePicker
                label="Select Date"
                value={selectedDate}
                onChange={(newValue) => {
                  if (newValue) {
                    setSelectedDate(newValue as Date);
                  }
                }}
                renderInput={(params) => (
                  <TextField {...params} fullWidth size="small" />
                )}
              />
            </Grid>
            {/* Hide machine filter for machine operators - they only see their assigned machine */}
            {user?.role !== 'machine' && (
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Filter by Machine"
                  value={filterMachine}
                  onChange={(e) => setFilterMachine(e.target.value)}
                >
                  <MenuItem value="All">All Machines</MenuItem>
                  {machines.map((m) => (
                    <MenuItem key={m} value={m}>
                      {m}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            <Grid item xs={12} sm={12} md={4}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchEntries}
                fullWidth
                sx={{ borderColor: '#00695C', color: '#00695C' }}
              >
                Refresh
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Timeline View */}
        <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <ChartIcon sx={{ color: '#00695C' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Timeline — Production Day: {format(selectedDate, 'MMM dd')} 07:30 → {format(addDays(selectedDate, 1), 'MMM dd, yyyy')} 07:30
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Production day: 07:30 to 07:30 (next day)
          </Typography>

          {filterMachine === 'All' ? (
            // Show timeline for all machines
            machines.map((machineName) => {
              const machineEntries = entries.filter((e) => e.machine === machineName);
              if (machineEntries.length === 0) return null;

              return (
                <Box key={machineName} sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#00695C' }}>
                    {machineName}
                  </Typography>
                  <Box sx={{ position: 'relative', height: 40, backgroundColor: '#f5f5f5', borderRadius: 1, mb: 1 }}>
                    {machineEntries.map((entry) => renderTimelineBar(entry))}
                  </Box>
                </Box>
              );
            })
          ) : (
            // Show timeline for selected machine
            <Box>
              <Box sx={{ position: 'relative', height: 60, backgroundColor: '#f5f5f5', borderRadius: 1, mb: 2 }}>
                {filteredEntries.map((entry) => renderTimelineBar(entry))}
              </Box>
            </Box>
          )}

          {/* Time markers */}
          <Box sx={{ position: 'relative', height: 20, mt: 1 }}>
            {[
              { label: '07:30', position: 0 },
              { label: '13:30', position: 25 },
              { label: '19:30', position: 50 },
              { label: '01:30', position: 75 },
              { label: '07:30', position: 100 },
            ].map((marker, index) => (
              <Box
                key={index}
                sx={{
                  position: 'absolute',
                  left: `${marker.position}%`,
                  transform: 'translateX(-50%)',
                  fontSize: '0.75rem',
                  color: '#666',
                }}
              >
                {marker.label}
              </Box>
            ))}
          </Box>

          {/* Legend */}
          <Box sx={{ mt: 3, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Chip label="Cleaning/TPM" size="small" sx={{ backgroundColor: '#4CAF50', color: 'white' }} />
            <Chip label="Printing/Running" size="small" sx={{ backgroundColor: '#2196F3', color: 'white' }} />
            <Chip label="Waiting" size="small" sx={{ backgroundColor: '#FF9800', color: 'white' }} />
            <Chip label="Repair/Damage" size="small" sx={{ backgroundColor: '#F44336', color: 'white' }} />
            <Chip label="Color Check" size="small" sx={{ backgroundColor: '#9C27B0', color: 'white' }} />
            <Chip label="Less Work" size="small" sx={{ backgroundColor: '#FFD700' }} />
            <Chip label="Other" size="small" sx={{ backgroundColor: '#607D8B', color: 'white' }} />
          </Box>
        </Paper>

        {/* Table View */}
        {viewMode === 'table' && (
          <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <TableIcon sx={{ color: '#00695C' }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Data Table — Production Day: {format(selectedDate, 'MMM dd')} 07:30 → {format(addDays(selectedDate, 1), 'MMM dd, yyyy')} 07:30
              </Typography>
            </Box>

            {loading && <Typography>Loading...</Typography>}

            {!loading && filteredEntries.length === 0 && (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">No entries found for this date</Typography>
              </Box>
            )}

            {!loading && filteredEntries.length > 0 && (
              <>
                {/* Desktop Table View */}
                {!isMobile ? (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                          <TableCell sx={{ fontWeight: 700 }}>Machine</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Job Type</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Start Time</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Stop Time</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Duration</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Artwork No.</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Batch No.</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>EPF No.</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Speed</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Quantity</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Created By</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Created At</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 700 }}>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredEntries
                          .slice()
                          .sort((a, b) => toProductionMinutes(a.time_start) - toProductionMinutes(b.time_start))
                          .map((entry) => (
                            <TableRow
                              key={entry.id}
                              sx={{
                                '&:hover': { backgroundColor: '#f9f9f9' },
                                borderLeft: `4px solid ${getJobColor(entry.job_type)}`,
                              }}
                            >
                              <TableCell>
                                <Chip
                                  label={entry.machine}
                                  size="small"
                                  sx={{ backgroundColor: '#00695C', color: 'white', fontSize: '0.75rem' }}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                  {entry.job_type}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                  {entry.time_start}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                  {entry.time_stop}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#00695C' }}>
                                  {entry.duration_minutes} min
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">{entry.artwork_no || '-'}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">{entry.batch_no || '-'}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">{entry.epf_number || '-'}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">{entry.machine_speed || '-'}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2' }}>
                                  {entry.quantity || '-'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption" color="text.secondary">
                                  {entry.created_by || '-'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption" color="text.secondary">
                                  {entry.created_at ? format(new Date(entry.created_at), 'MMM dd, HH:mm') : '-'}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                  {(user?.role === 'admin' || user?.role === 'manager') && (
                                    <IconButton
                                      size="small"
                                      sx={{ color: '#00695C' }}
                                      onClick={() => handleOpenEditDialog(entry)}
                                      title="Edit entry"
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  )}
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleDelete(entry.id!)}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  /* Mobile Card View */
                  <Stack spacing={2}>
                    {filteredEntries
                      .slice()
                      .sort((a, b) => toProductionMinutes(a.time_start) - toProductionMinutes(b.time_start))
                      .map((entry) => (
                        <Card
                          key={entry.id}
                          sx={{
                            borderLeft: `6px solid ${getJobColor(entry.job_type)}`,
                            '&:hover': { boxShadow: 6 },
                          }}
                        >
                          <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                <Chip
                                  icon={<MachineIcon />}
                                  label={entry.machine}
                                  size="small"
                                  sx={{ backgroundColor: '#00695C', color: 'white' }}
                                />
                                {entry.date !== format(selectedDate, 'yyyy-MM-dd') && (
                                  <Chip label="Next Day" size="small" sx={{ backgroundColor: '#9C27B0', color: 'white', fontSize: '0.7rem' }} />
                                )}
                              </Box>
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                {(user?.role === 'admin' || user?.role === 'manager') && (
                                  <IconButton size="small" sx={{ color: '#00695C' }} onClick={() => handleOpenEditDialog(entry)} title="Edit entry">
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                )}
                                <IconButton size="small" color="error" onClick={() => handleDelete(entry.id!)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </Box>

                            <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                              {entry.job_type}
                            </Typography>

                            <Divider sx={{ my: 1.5 }} />

                            <Grid container spacing={1}>
                              <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Start Time
                                </Typography>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                                  {entry.time_start}
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Stop Time
                                </Typography>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                                  {entry.time_stop}
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Duration
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#00695C' }}>
                                  {entry.duration_minutes} min
                                </Typography>
                              </Grid>
                              {entry.artwork_no && (
                                <>
                                  <Grid item xs={6}>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      Artwork No.
                                    </Typography>
                                    <Typography variant="body2">{entry.artwork_no}</Typography>
                                  </Grid>
                                  <Grid item xs={6}>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      Batch No.
                                    </Typography>
                                    <Typography variant="body2">{entry.batch_no}</Typography>
                                  </Grid>
                                  <Grid item xs={6}>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      EPF No.
                                    </Typography>
                                    <Typography variant="body2">{entry.epf_number || '-'}</Typography>
                                  </Grid>
                                  <Grid item xs={6}>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      Speed
                                    </Typography>
                                    <Typography variant="body2">{entry.machine_speed}</Typography>
                                  </Grid>
                                  <Grid item xs={6}>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      Quantity
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2' }}>
                                      {entry.quantity || '-'}
                                    </Typography>
                                  </Grid>
                                </>
                              )}
                              {entry.created_by && (
                                <Grid item xs={6}>
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    Created By
                                  </Typography>
                                  <Typography variant="body2">
                                    {entry.created_by}
                                  </Typography>
                                </Grid>
                              )}
                              {entry.created_at && (
                                <Grid item xs={6}>
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    Created At
                                  </Typography>
                                  <Typography variant="body2">
                                    {format(new Date(entry.created_at), 'MMM dd, yyyy HH:mm')}
                                  </Typography>
                                </Grid>
                              )}
                            </Grid>
                          </CardContent>
                        </Card>
                      ))}
                  </Stack>
                )}
              </>
            )}
          </Paper>
        )}

        {/* Summary Entry Cards (Only show for timeline view) */}
        {viewMode === 'timeline' && (
        <>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Entries — Production Day: {format(selectedDate, 'MMM dd')} 07:30 → {format(addDays(selectedDate, 1), 'MMM dd, yyyy')} 07:30
        </Typography>

        {loading && <Typography>Loading...</Typography>}

        {!loading && filteredEntries.length === 0 && (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">No entries found for this date</Typography>
          </Paper>
        )}

        <Grid container spacing={2}>
          {filteredEntries.map((entry) => (
            <Grid item xs={12} sm={6} md={4} key={entry.id}>
              <Card
                sx={{
                  borderLeft: `6px solid ${getJobColor(entry.job_type)}`,
                  '&:hover': { boxShadow: 6 },
                  transition: 'box-shadow 0.2s',
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Chip
                      icon={<MachineIcon />}
                      label={entry.machine}
                      size="small"
                      sx={{ backgroundColor: '#00695C', color: 'white' }}
                    />
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {(user?.role === 'admin' || user?.role === 'manager') && (
                        <IconButton size="small" sx={{ color: '#00695C' }} onClick={() => handleOpenEditDialog(entry)} title="Edit entry">
                          <EditIcon fontSize="small" />
                        </IconButton>
                      )}
                      <IconButton size="small" color="error" onClick={() => handleDelete(entry.id!)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>

                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#333' }}>
                    {entry.job_type}
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <TimeIcon fontSize="small" sx={{ color: '#666' }} />
                    <Typography variant="body2" color="text.secondary">
                      {entry.time_start} - {entry.time_stop}
                    </Typography>
                  </Box>

                  <Typography variant="caption" color="text.secondary">
                    Duration: {entry.duration_minutes} min
                  </Typography>

                  {entry.artwork_no && (
                    <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #eee' }}>
                      <Typography variant="caption" display="block" color="text.secondary">
                        Artwork: {entry.artwork_no}
                      </Typography>
                      <Typography variant="caption" display="block" color="text.secondary">
                        Batch: {entry.batch_no}
                      </Typography>
                      <Typography variant="caption" display="block" color="text.secondary">
                        Speed: {entry.machine_speed}
                      </Typography>
                    </Box>
                  )}

                  {entry.created_at && (
                    <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #eee' }}>
                      <Typography variant="caption" display="block" color="text.secondary">
                        Created: {format(new Date(entry.created_at), 'MMM dd, yyyy HH:mm')}
                      </Typography>
                      {entry.created_by && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          By: {entry.created_by}
                        </Typography>
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        </>
        )}

        {/* Job Comparison CSV Export Dialog */}
        <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ backgroundColor: '#00695C', color: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DownloadIcon />
              Export Job Comparison CSV
            </Box>
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            {exportError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setExportError(null)}>
                {exportError}
              </Alert>
            )}
            <Stack spacing={2}>
              {/* Machine */}
              <TextField
                select fullWidth size="small" label="Machine"
                value={exportMachine}
                onChange={(e) => { setExportMachine(e.target.value); setExportGroup1Jobs([]); setExportGroup2Jobs([]); }}
              >
                {machines.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </TextField>

              {/* Date range */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <DatePicker
                  label="Start Date" value={exportStartDate}
                  onChange={(v) => { if (v) setExportStartDate(v as Date); }}
                  renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                />
                <DatePicker
                  label="End Date" value={exportEndDate}
                  onChange={(v) => { if (v) setExportEndDate(v as Date); }}
                  renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                />
              </Box>

              {/* Group names */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField fullWidth size="small" label="Group 1 Name" value={exportGroup1Name}
                  onChange={(e) => setExportGroup1Name(e.target.value)} />
                <TextField fullWidth size="small" label="Group 2 Name" value={exportGroup2Name}
                  onChange={(e) => setExportGroup2Name(e.target.value)} />
              </Box>

              {/* Group 2 mode */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant={exportGroup2Mode === 'all-others' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => { setExportGroup2Mode('all-others'); setExportGroup2Jobs([]); }}
                  sx={{ flex: 1, fontSize: '0.78rem',
                    ...(exportGroup2Mode === 'all-others'
                      ? { backgroundColor: '#00695C', '&:hover': { backgroundColor: '#004d40' } }
                      : { borderColor: '#00695C', color: '#00695C' }) }}
                >
                  Group 2 = All other jobs
                </Button>
                <Button
                  variant={exportGroup2Mode === 'manual' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setExportGroup2Mode('manual')}
                  sx={{ flex: 1, fontSize: '0.78rem',
                    ...(exportGroup2Mode === 'manual'
                      ? { backgroundColor: '#00695C', '&:hover': { backgroundColor: '#004d40' } }
                      : { borderColor: '#00695C', color: '#00695C' }) }}
                >
                  Group 2 = Select jobs
                </Button>
              </Box>

              {/* Job type assignment */}
              {exportMachine && (
                <Box>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                    Assign job types to groups
                    {exportGroup2Mode === 'all-others' && (
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        (unselected jobs → Group 2)
                      </Typography>
                    )}
                  </Typography>
                  <Box sx={{ maxHeight: 260, overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                    {exportAvailableJobs.map(job => {
                      const bucket = getJobExportBucket(job);
                      return (
                        <Box key={job} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, py: 0.25 }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" sx={{ fontSize: '0.82rem' }}>{job}</Typography>
                          </Box>
                          <Button
                            size="small"
                            variant={bucket === 'g1' ? 'contained' : 'outlined'}
                            onClick={() => toggleExportJob(job, 1)}
                            sx={{ minWidth: 70, fontSize: '0.72rem', py: 0.25,
                              ...(bucket === 'g1'
                                ? { backgroundColor: '#00695C', '&:hover': { backgroundColor: '#004d40' } }
                                : { borderColor: '#00695C', color: '#00695C' }) }}
                          >
                            {exportGroup1Name || 'Group 1'}
                          </Button>
                          {exportGroup2Mode === 'manual' && (
                            <Button
                              size="small"
                              variant={bucket === 'g2' ? 'contained' : 'outlined'}
                              onClick={() => toggleExportJob(job, 2)}
                              sx={{ minWidth: 70, fontSize: '0.72rem', py: 0.25,
                                ...(bucket === 'g2'
                                  ? { backgroundColor: '#1976d2', '&:hover': { backgroundColor: '#115293' } }
                                  : { borderColor: '#1976d2', color: '#1976d2' }) }}
                            >
                              {exportGroup2Name || 'Group 2'}
                            </Button>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Group 1: {exportGroup1Jobs.length} job{exportGroup1Jobs.length !== 1 ? 's' : ''} selected
                    {exportGroup2Mode === 'manual' && ` · Group 2: ${exportGroup2Jobs.length} job${exportGroup2Jobs.length !== 1 ? 's' : ''} selected`}
                  </Typography>
                </Box>
              )}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setExportDialogOpen(false)} color="inherit">Cancel</Button>
            <Button
              onClick={handleExportCSV}
              variant="contained"
              disabled={exportLoading}
              startIcon={<DownloadIcon />}
              sx={{ backgroundColor: '#00695C', '&:hover': { backgroundColor: '#004d40' } }}
            >
              {exportLoading ? 'Generating...' : 'Download CSV'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* OEE Export Dialog */}
        <Dialog open={oeeExportDialogOpen} onClose={() => setOeeExportDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle sx={{ backgroundColor: '#00695C', color: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DownloadIcon />
              Export OEE Data to Excel
            </Box>
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            {oeeExportError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setOeeExportError(null)}>
                {oeeExportError}
              </Alert>
            )}
            <Stack spacing={2}>
              {/* Machine Dropdown Selection */}
              <TextField
                select
                fullWidth
                size="small"
                label="Select Machines"
                value=""
                SelectProps={{
                  multiple: true,
                  value: oeeExportMachines,
                  onChange: (e) => {
                    const value = e.target.value as string[];
                    setOeeExportMachines(value);
                  },
                  renderValue: (selected: any) => {
                    const sel = selected as string[];
                    return sel.length === 0 ? 'None selected' : `${sel.length} machine${sel.length !== 1 ? 's' : ''} selected`;
                  },
                }}
              >
                {machines.map((machine) => (
                  <MenuItem key={machine} value={machine}>
                    <Checkbox checked={oeeExportMachines.indexOf(machine) > -1} />
                    <ListItemText primary={machine} />
                  </MenuItem>
                ))}
              </TextField>

              {/* Month/Year Picker */}
              <DatePicker
                label="Select Month"
                views={['year', 'month']}
                value={oeeExportMonth}
                onChange={(v) => { if (v) setOeeExportMonth(v as Date); }}
                renderInput={(params) => <TextField {...params} fullWidth size="small" />}
              />

              {/* Job Categorization Instructions */}
              <Alert severity="info" sx={{ fontSize: '0.85rem' }}>
                Categorize job types for accurate OEE calculation:
                <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                  <li><strong>Running</strong>: Production activities (actual operating time)</li>
                  <li><strong>PD</strong>: Planned downtime activities</li>
                  <li><strong>Loss</strong>: Unplanned downtime (reduces availability)</li>
                  <li>Uncategorized jobs are automatically excluded from OEE calculation</li>
                </ul>
              </Alert>

              {/* Job type categorization */}
              {oeeExportMachines.length > 0 && (
                <Box>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                    Categorize Job Types (from all selected machines)
                  </Typography>
                  <Box sx={{ maxHeight: 320, overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                    {oeeAvailableJobs.map(job => {
                      const category = getOeeJobCategory(job);
                      return (
                        <Box key={job} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, py: 0.5, borderBottom: '1px solid #f0f0f0' }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>{job}</Typography>
                          </Box>
                          <Button
                            size="small"
                            variant={category === 'running' ? 'contained' : 'outlined'}
                            onClick={() => toggleOeeJob(job, 'running')}
                            sx={{ 
                              minWidth: 80, 
                              fontSize: '0.72rem', 
                              py: 0.4,
                              ...(category === 'running'
                                ? { backgroundColor: '#00695C', color: 'white', '&:hover': { backgroundColor: '#004d40' } }
                                : { borderColor: '#00695C', color: '#00695C' }) 
                            }}
                          >
                            Running
                          </Button>
                          <Button
                            size="small"
                            variant={category === 'loss' ? 'contained' : 'outlined'}
                            onClick={() => toggleOeeJob(job, 'loss')}
                            sx={{ 
                              minWidth: 80, 
                              fontSize: '0.72rem', 
                              py: 0.4,
                              ...(category === 'loss'
                                ? { backgroundColor: '#f44336', color: 'white', '&:hover': { backgroundColor: '#d32f2f' } }
                                : { borderColor: '#f44336', color: '#f44336' }) 
                            }}
                          >
                            Loss
                          </Button>
                          <Button
                            size="small"
                            variant={category === 'pd' ? 'contained' : 'outlined'}
                            onClick={() => toggleOeeJob(job, 'pd')}
                            sx={{
                              minWidth: 80,
                              fontSize: '0.72rem',
                              py: 0.4,
                              ...(category === 'pd'
                                ? { backgroundColor: '#ff9800', color: 'white', '&:hover': { backgroundColor: '#ef6c00' } }
                                : { borderColor: '#ff9800', color: '#ff9800' })
                            }}
                          >
                            PD
                          </Button>
                        </Box>
                      );
                    })}
                  </Box>

                  {/* Category Summary */}
                  <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip 
                      label={`Running: ${oeeRunningJobs.length}`} 
                      size="small" 
                      sx={{ backgroundColor: '#00695C', color: 'white', fontWeight: 600 }}
                    />
                    <Chip 
                      label={`Loss: ${oeeLossJobs.length}`} 
                      size="small" 
                      sx={{ backgroundColor: '#f44336', color: 'white', fontWeight: 600 }}
                    />
                    <Chip 
                      label={`PD: ${oeePdJobs.length}`} 
                      size="small" 
                      sx={{ backgroundColor: '#ff9800', color: 'white', fontWeight: 600 }}
                    />
                    <Chip 
                      label={`Uncategorized: ${oeeAvailableJobs.length - oeeRunningJobs.length - oeeLossJobs.length - oeePdJobs.length}`} 
                      size="small" 
                      sx={{ backgroundColor: '#9E9E9E', color: 'white', fontWeight: 600 }}
                    />
                  </Box>
                </Box>
              )}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setOeeExportDialogOpen(false)} color="inherit">Cancel</Button>
            <Button
              onClick={handleExportOEE}
              variant="contained"
              disabled={oeeExportLoading}
              startIcon={<DownloadIcon />}
              sx={{ backgroundColor: '#00695C', '&:hover': { backgroundColor: '#004d40' } }}
            >
              {oeeExportLoading ? 'Generating...' : 'Download Excel'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Entry Dialog */}
        <Dialog
          open={editDialogOpen}
          onClose={handleCloseEditDialog}
          maxWidth="sm"
          fullWidth
          fullScreen={window.innerWidth < 600}
        >
          <DialogTitle sx={{ backgroundColor: '#00695C', color: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EditIcon />
              Edit Machine Efficiency Entry
            </Box>
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Collapse in={!!error}>
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            </Collapse>
            <Stack spacing={2.5}>
              <TextField
                select
                fullWidth
                label="Machine"
                value={machine}
                onChange={(e) => { setMachine(e.target.value); setJobType(''); }}
                required
                size="small"
              >
                {machines.map((m) => (
                  <MenuItem key={m} value={m}>{m}</MenuItem>
                ))}
              </TextField>
              <TextField
                select
                fullWidth
                label="Job Type"
                value={jobType}
                onChange={(e) => setJobType(e.target.value)}
                required
                size="small"
                disabled={!machine}
              >
                {(machine ? (machineJobTypes[machine] || []) : []).map((job) => (
                  <MenuItem key={job} value={job}>{job}</MenuItem>
                ))}
              </TextField>
              <DatePicker
                label="Date"
                value={editDate}
                onChange={(newValue) => { if (newValue) setEditDate(newValue as Date); }}
                renderInput={(params) => (
                  <TextField {...params} fullWidth required size="small" helperText="Change to correct the date" />
                )}
              />
              <TimePicker
                label="Time Start"
                value={timeStart}
                onChange={setTimeStart}
                ampm={false}
                renderInput={(params) => (
                  <TextField {...params} fullWidth required size="small" />
                )}
              />
              <TimePicker
                label="Time Stop"
                value={timeStop}
                onChange={setTimeStop}
                ampm={false}
                renderInput={(params) => (
                  <TextField {...params} fullWidth required size="small" />
                )}
              />
              {requiresProductionInputs && (
                <>
                  <Divider sx={{ my: 1 }}>
                    <Chip label="Production Details" size="small" />
                  </Divider>
                  <TextField
                    fullWidth label="Artwork No." value={artworkNo}
                    onChange={(e) => setArtworkNo(e.target.value)} required size="small"
                  />
                  <TextField
                    fullWidth label="Batch No." value={batchNo}
                    onChange={(e) => setBatchNo(e.target.value)} required size="small"
                  />
                  <TextField
                    fullWidth label="Employee EPF Number" value={epfNumber}
                    onChange={(e) => setEpfNumber(e.target.value)} size="small"
                    helperText="Optional — Employee Provident Fund number"
                  />
                  <TextField
                    fullWidth label="Machine Speed (m/hr)" value={machineSpeed}
                    onChange={(e) => setMachineSpeed(e.target.value)}
                    type="number" required size="small"
                  />
                  <TextField
                    fullWidth label="Quantity (m)" value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    type="number" required size="small"
                  />
                </>
              )}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseEditDialog} color="inherit">Cancel</Button>
            <Button
              onClick={handleEditSubmit}
              variant="contained"
              disabled={loading}
              sx={{ backgroundColor: '#00695C', '&:hover': { backgroundColor: '#004d40' } }}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Entry Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
          fullScreen={window.innerWidth < 600}
        >
          <DialogTitle sx={{ backgroundColor: '#00695C', color: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AddIcon />
              Add Machine Efficiency Entry
            </Box>
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Collapse in={!!error}>
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            </Collapse>

            <Stack spacing={2.5}>
              <TextField
                select
                fullWidth
                label="Machine"
                value={machine}
                onChange={(e) => {
                  setMachine(e.target.value);
                  setJobType(''); // Reset job type when machine changes
                }}
                required
                size="small"
                disabled={user?.role === 'machine' && !!user?.assigned_machine}
                helperText={
                  user?.role === 'machine' && user?.assigned_machine
                    ? `You are assigned to: ${user.assigned_machine}` 
                    : user?.role === 'machine' && !user?.assigned_machine
                    ? 'Please contact admin to assign you to a machine'
                    : undefined
                }
              >
                {machines.map((m) => (
                  <MenuItem key={m} value={m}>
                    {m}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                fullWidth
                label="Job Type"
                value={jobType}
                onChange={(e) => setJobType(e.target.value)}
                required
                size="small"
                disabled={!machine}
                helperText={!machine ? 'Please select a machine first' : undefined}
              >
                {availableJobTypes.map((job) => (
                  <MenuItem key={job} value={job}>
                    {job}
                  </MenuItem>
                ))}
              </TextField>

              <TimePicker
                label="Time Start"
                value={timeStart}
                onChange={setTimeStart}
                ampm={false}
                renderInput={(params) => (
                  <TextField {...params} fullWidth required size="small" />
                )}
              />

              <TimePicker
                label="Time Stop"
                value={timeStop}
                onChange={setTimeStop}
                ampm={false}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    required
                    size="small"
                    helperText={(() => {
                      if (!timeStop || !timeStart) return undefined;
                      const stopMin  = timeStop.getHours()  * 60 + timeStop.getMinutes();
                      const startMin = timeStart.getHours() * 60 + timeStart.getMinutes();
                      const crossesMidnight = timeStop <= timeStart && startMin >= PRODUCTION_DAY_RESET_MINUTES && stopMin < PRODUCTION_DAY_RESET_MINUTES;
                      if (crossesMidnight)
                        return `Crosses midnight — will auto-split: ${format(timeStart, 'HH:mm')}–23:59 on ${format(selectedDate, 'MMM dd')} and 00:00–${format(timeStop, 'HH:mm')} on ${format(addDays(selectedDate, 1), 'MMM dd')}`;
                      if (stopMin < PRODUCTION_DAY_RESET_MINUTES)
                        return `Night-shift time — will be saved under ${format(addDays(selectedDate, 1), 'MMM dd')}`;
                      return undefined;
                    })()}
                  />
                )}
              />

              {requiresProductionInputs && (
                <>
                  <Divider sx={{ my: 1 }}>
                    <Chip label="Production Details" size="small" />
                  </Divider>

                  <TextField
                    fullWidth
                    label="Artwork No."
                    value={artworkNo}
                    onChange={(e) => setArtworkNo(e.target.value)}
                    required
                    size="small"
                  />

                  <TextField
                    fullWidth
                    label="Batch No."
                    value={batchNo}
                    onChange={(e) => setBatchNo(e.target.value)}
                    required
                    size="small"
                  />

                  <TextField
                    fullWidth
                    label="Employee EPF Number"
                    value={epfNumber}
                    onChange={(e) => setEpfNumber(e.target.value)}
                    size="small"
                    helperText="Optional — Employee Provident Fund number"
                  />

                  <TextField
                    fullWidth
                    label="Machine Speed (m/hr)"
                    value={machineSpeed}
                    onChange={(e) => setMachineSpeed(e.target.value)}
                    type="number"
                    required
                    size="small"
                  />

                  <TextField
                    fullWidth
                    label="Quantity (m)"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    type="number"
                    required
                    size="small"
                    helperText="Actual output quantity in metres"
                  />
                </>
              )}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseDialog} color="inherit">
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={loading}
              sx={{ backgroundColor: '#00695C', '&:hover': { backgroundColor: '#004d40' } }}
            >
              {loading ? 'Adding...' : 'Add Entry'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </LocalizationProvider>
  );
};

export default MachineEfficiency;
